import React, { useEffect, useMemo, useRef, useState } from "react";
import apiRequest from "utils/ApiRequest/apiRequest.js";
import Login from "components/Structure/Login/Login.js";
import Button from "components/Elements/Button/Button.js";
import useOnClickOutside from "hooks/useOnClickOutside.js";
import useMediaQueries from "hooks/useMediaQueries.js";
import Icon from "components/Elements/Icon/Icon.tsx";
import Signup from "components/Structure/Signup/Signup.js";
import SuspendedUserModal from "components/Reusable/Modals/SuspendedUserModal.tsx";
import Image from "components/Elements/Image/Image.tsx";
import { useModal } from "contexts/modalContext/modalContext.js";
import { useContent } from "contexts/contentContext/contentContext.js";
import { LinkToNewTab } from "components/Elements/Link/Link.js";
import { convertMiliseconds } from "utils/convertMiliseconds.js";
import { useToasts } from "react-toast-notifications";
import Recaptcha from "components/Reusable/Recaptcha/Recaptcha.tsx";
import { awaiter } from "components/Pages/Home/Helpers/helpers.js";
import { RECAPTCHA_APPEAR_DELAY } from "utils/globalConsts.ts";

import "./emailUserExistModal.scss";

const SucceedVerificationContent = ({ isConfirmEmail, email }) => {
  const { getTextByKey } = useContent();
  const { closeModal, setModal } = useModal();

  const handleSignUp = () => {
    setModal({
      modal: Signup,
      modalSize: "full-modal",
      props: {
        email: email,
        autoConfirm: true,
      },
    });
  };

  useEffect(() => {
    if (isConfirmEmail) {
      setTimeout(() => {
        closeModal();
      }, 4000);
    }
  }, [isConfirmEmail]);

  return (
    <>
      <div className="close-button">
        <Icon name="x" onClick={closeModal} white />
      </div>
      <div className="user-email-exist-modal-header">
        <div className="sub-text title">
          {getTextByKey("validation-completed")}
        </div>
      </div>
      <div className="user-email-exist-modal-body">
        <div className="verified-icon">
          <Icon name="check" />
        </div>
      </div>
      {!isConfirmEmail ? (
        <div className="user-email-exist-modal-footer">
          <div className="sub-text">
            {getTextByKey("email-verified-call-to-action-text")}
          </div>
          <Button onClick={handleSignUp} blank>
            {getTextByKey("create-free-account")}
          </Button>
        </div>
      ) : null}
    </>
  );
};

const EmailUserExistModalHeader = ({
  showEnterCode,
  email,
  isConfirmEmail,
}) => {
  const { getTextByKey } = useContent();
  const { setModal } = useModal();

  const handleOpenLogin = () => {
    setModal({
      modal: Login,
      modalSize: "full-modal",
    });
  };

  return (
    <div className="user-email-exist-modal-header">
      <div className={`sub-text${showEnterCode ? " title" : ""}`}>
        {getTextByKey(showEnterCode ? "verify-your-email" : null)}
      </div>
      <div className="sub-text">
        {showEnterCode ? (
          <>
            {getTextByKey("we-sent-code-to")} {email}
          </>
        ) : !isConfirmEmail ? (
          <>
            <span>{getTextByKey("verify-your-email-text")}</span>
            <div className="login-text-wrapper">
              <span>
                <span className="login-word" onClick={handleOpenLogin}>
                  {getTextByKey("login")}
                </span>
                <span>{getTextByKey("login-to-your-account")}</span>
              </span>
              <div className="or-word">{getTextByKey("or")}</div>
            </div>
          </>
        ) : (
          getTextByKey("confirm-your-email-message")
        )}
      </div>
    </div>
  );
};

const EmailUserExistModal = ({
  email,
  setMailVerifying,
  isConfirmEmail,
  callback,
  setMailVerifyingProcess,
  getMailVerifyingProcess,
  mailId,
}) => {
  const {
    getTextByKey,
    getLanguageCulture,
    getDirection,
    getBasicSettingsData,
  } = useContent();
  const { setModal, closeModal } = useModal();
  const { mobileCondition, tabletCondition } = useMediaQueries();
  const { addToast } = useToasts();
  const [throttleError, setThrottleError] = useState(null);

  // calculate how much time left from user send request to verify email
  const timeLeft = useMemo(() => {
    return getMailVerifyingProcess().time
      ? 3600000 - (Date.now() - getMailVerifyingProcess().time)
      : null;
  }, [getMailVerifyingProcess().time]);

  // if user send request to verify email and time left is more than 0 show already verifying code input
  const [showEnterCode, setShowEnterCode] = useState(timeLeft ? true : false);
  const [success, setSuccess] = useState(false);
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [timer, setTimer] = useState(timeLeft || 3600000);
  const [sessionId, setSessionId] = useState(
    getMailVerifyingProcess().sessionId
  );
  const [wrongCode, setWrongCode] = useState(false);
  const [cords, setCords] = useState({});

  // func to generate random session id
  const generateSessionId = () => {
    return (
      Math.random().toString(36).substring(2, 15) +
      Math.random().toString(36).substring(2, 15)
    );
  };

  // handle user enter code and send request to verify code
  useEffect(() => {
    if (code.length >= 6) {
      handleVerificateCode();
    }
    setWrongCode(false);
  }, [code]);

  const handleOpenLogin = () => {
    setModal({
      modal: Login,
      modalSize: "full-modal",
    });
  };

  const [isCaptchaShown, setIsCaptchaShown] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);

  const handleSendCode = async () => {
    setLoading(true);
    const currentSessionId = generateSessionId();

    let token = captchaToken;

    if (!token) {
      setIsCaptchaShown(true);
      // try {
      if (getBasicSettingsData()?.recaptchaType === "google") {
        await awaiter(RECAPTCHA_APPEAR_DELAY);
        await recaptchaRef.current.executeAsync();
      }
      // } catch (err) {
      //   addToast(getTextByKey("recaptcha-token-cant-execute"), {
      //     appearance: "error",
      //   });
      //   token = false;
      // }

      return;
    }

    const res = await apiRequest(
      "UserVerifiedCode",
      "CreateNewVerifiedCode",
      {
        session_id: currentSessionId,
        sender_email_address: email,
        culture: getLanguageCulture(),
        isV3Recaptcha: true,
        recaptchaResponse: token,
        mailId: mailId,
        // captchaServiceName: "cloudflare",
      },
      "POST"
    );

    if (res.status === "success") {
      setSessionId(currentSessionId);
      setTimer(3600000);
      setMailVerifyingProcess({
        time: Date.now(),
        sessionId: currentSessionId,
      });
      setShowEnterCode(true);
    }

    if (res.status === "error") {
      const errMessage = res.data;
      setThrottleError(errMessage);
      if (errMessage === "Error #4200") {
        setModal({
          modal: SuspendedUserModal,
        });
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    if (captchaToken) {
      handleSendCode();

      setCaptchaToken(null);
      setIsCaptchaShown(false);
    }
  }, [captchaToken]);

  // clearing error logic
  useEffect(() => {
    if (throttleError) {
      const timer = setTimeout(() => {
        setThrottleError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [throttleError]);

  // counter for 1 hour
  useEffect(() => {
    if (showEnterCode) {
      const intervalId = setInterval(() => {
        if (timer <= 0) {
          setTimer(0);
        } else {
          setTimer((prev) => prev - 1000);
        }
      }, 1000);
      return () => clearInterval(intervalId);
    }
  }, [showEnterCode]);

  // verify code
  const handleVerificateCode = async () => {
    setLoading(true);
    const res = await apiRequest(
      "UserVerifiedCode",
      "CheckVerificationCode",
      {
        session_id: sessionId,
        code: code,
        userEmail: email,
        mailId: mailId,
      },
      "POST"
    );
    if (res.status === "success") {
      if (res.data) {
        callback();
        setSuccess(true);
      } else {
        setWrongCode(true);
      }
    }
    setLoading(false);
  };

  const modalWindowRef = useRef(null);
  useOnClickOutside(modalWindowRef, () => {
    closeModal();
  });

  const handlePosition = () => {
    if (mobileCondition) {
      return;
    }
    if (tabletCondition) {
      // Place beneath the input
      const inputElemPos = document
        .getElementById("SendOwnerEmail-id")
        ?.getBoundingClientRect();
      if (modalWindowRef.current && inputElemPos) {
        const windowCoords = modalWindowRef.current?.getBoundingClientRect();
        if (!windowCoords) return;
        // get size of the modal window
        const width = windowCoords.width;
        const height = windowCoords.height;

        // put it left from the input which is relativeCords.left and relativeCords.top
        // and put it in the middle of the input
        const left = window.innerWidth / 2 - width / 2;
        const top = inputElemPos.bottom + inputElemPos.height + window.scrollY;
        setCords({ left, top });
        return;
      }
    }
    const inputElemPos = document
      .getElementById("SendOwnerEmail-id")
      ?.getBoundingClientRect();
    if (modalWindowRef.current && inputElemPos && cords.left === undefined) {
      const windowCoords = modalWindowRef.current?.getBoundingClientRect();
      if (!windowCoords) return;
      // get size of the modal window
      const width = windowCoords.width;
      const height = windowCoords.height;

      // put it left from the input which is relativeCords.left and relativeCords.top
      // and put it in the middle of the input
      const left =
        getDirection() === "rtl"
          ? inputElemPos.left + width + 10
          : inputElemPos.left - width - 10;
      const top =
        inputElemPos.top +
        inputElemPos.height / 2 -
        height / 2 +
        window.scrollY;
      setCords({ left, top });
    }
  };

  useEffect(() => {
    handlePosition();
  }, [success, showEnterCode, sessionId]);

  useEffect(() => {
    document.activeElement.blur();
    return () => {
      setMailVerifying(false);
    };
  }, []);

  const recaptchaRef = useRef();

  return (
    <div
      className={`user-email-exist-modal ${getDirection()}-direction`}
      style={cords}
      ref={modalWindowRef}
    >
      {isCaptchaShown ? (
        <Recaptcha
          recaptchaRef={recaptchaRef}
          onSuccess={(token) => setCaptchaToken(token)}
        />
      ) : null}
      {loading ? (
        <Image
          className="loader-animation-gif"
          name="preloader"
          ext="gif"
          alt=""
          title=""
        />
      ) : success ? (
        <SucceedVerificationContent
          email={email}
          isConfirmEmail={isConfirmEmail}
        />
      ) : (
        <>
          <EmailUserExistModalHeader
            {...{ showEnterCode, email, isConfirmEmail }}
          />
          <div className="user-email-exist-modal-body">
            {showEnterCode ? (
              <input
                value={code}
                onChange={(e) => {
                  setCode(e.target.value);
                }}
                placeholder={"* * * * * *"}
                disabled={timer <= 0}
              ></input>
            ) : isConfirmEmail ? (
              <>
                <div>
                  <div className="login-word" onClick={handleOpenLogin}>
                    {getTextByKey("login")}
                  </div>
                  {getTextByKey("login-to-your-account")}
                </div>
                <div className="or-word">{getTextByKey("or")}</div>
              </>
            ) : null}
          </div>
          <div className="user-email-exist-modal-footer">
            {throttleError ? (
              <div className="wrong-code">{getTextByKey(throttleError)}</div>
            ) : null}
            {showEnterCode ? (
              <>
                {wrongCode && code.length > 0 ? (
                  <div className="wrong-code">
                    {getTextByKey("error-invalid-code-message")}
                  </div>
                ) : null}
                <div className="resend-code" onClick={handleSendCode}>
                  {getTextByKey(
                    timer > 0
                      ? "resend-code-to-verify"
                      : "session-expired-action"
                  )}
                </div>
                {timer > 0 ? (
                  <div>
                    {convertMiliseconds({
                      miliseconds: timer,
                      showDays: false,
                      showHours: false,
                    })}{" "}
                    {getTextByKey("time-left-validation")}
                  </div>
                ) : (
                  <div>{getTextByKey("session-expired-message")}</div>
                )}
              </>
            ) : (
              <>
                <Button onClick={handleSendCode}>
                  {getTextByKey("send-verification-code")}
                </Button>
                <LinkToNewTab
                  page="support/sending-files/email-verification"
                  isNewTab={true}
                >
                  {getTextByKey("why-do-we-asked")}
                </LinkToNewTab>
              </>
            )}
          </div>
        </>
      )}
      <div className="tooltip-arrow" />
    </div>
  );
};

export default EmailUserExistModal;
